import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import Modal from '../ui/Modal';
import { migrateEmployee } from '../../api/migrations';
import { getActiveTools } from '../../api/tools';
import { ArrowRightLeft } from 'lucide-react';

const MigrateToolModal = ({ isOpen, onClose, onSaved, employee }) => {
  const [tools, setTools] = useState([]);
  const [form, setForm] = useState({ ai_tool_id: '', start_date: '', notes: '' });
  const [endDate, setEndDate] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && employee) {
      setForm({ ai_tool_id: '', start_date: '', notes: '' });
      setErrors({});
      setEndDate('');
      getActiveTools()
        .then((res) => {
          // Exclude current tool from choices
          const filtered = res.data.filter((t) => t.id !== employee.tool_id);
          setTools(filtered);
        })
        .catch(() => toast.error('Could not load tools.'));
    }
  }, [isOpen, employee]);

  useEffect(() => {
    if (form.start_date) {
      const d = new Date(form.start_date);
      d.setMonth(d.getMonth() + 1);
      setEndDate(d.toISOString().split('T')[0]);
    } else {
      setEndDate('');
    }
  }, [form.start_date]);

  const set = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!form.ai_tool_id) errs.ai_tool_id = 'Please select a tool to migrate to.';
    if (!form.start_date) errs.start_date = 'Start date is required.';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setLoading(true);
    try {
      await migrateEmployee({
        employee_id: employee.id,
        ai_tool_id: Number(form.ai_tool_id),
        start_date: form.start_date,
        notes: form.notes.trim() || undefined,
      });
      toast.success(`${employee.name} migrated successfully.`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Migration failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Migrate AI Tool">
      {employee && (
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Employee summary */}
          <div className="bg-gray-50 rounded-xl px-4 py-3 flex items-center gap-3">
            <div className="w-9 h-9 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-semibold text-primary-700">
                {employee.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{employee.name}</p>
              <p className="text-xs text-gray-400 truncate">{employee.email}</p>
            </div>
          </div>

          {/* From → To */}
          <div className="flex items-center gap-3">
            <div className="flex-1 bg-red-50 border border-red-100 rounded-xl px-3 py-2.5 text-center">
              <p className="text-xs text-gray-400 mb-1">Current Tool</p>
              <p className="text-sm font-semibold text-red-700 truncate">
                {employee.tool_name || 'None'}
              </p>
            </div>
            <ArrowRightLeft className="w-5 h-5 text-gray-400 flex-shrink-0" />
            <div className="flex-1 bg-green-50 border border-green-100 rounded-xl px-3 py-2.5 text-center">
              <p className="text-xs text-gray-400 mb-1">Migrating To</p>
              <p className="text-sm font-semibold text-green-700 truncate">
                {form.ai_tool_id
                  ? tools.find((t) => t.id === Number(form.ai_tool_id))?.name || '—'
                  : '—'}
              </p>
            </div>
          </div>

          {/* New tool */}
          <div>
            <label className="label">New AI Tool</label>
            <select
              className="input"
              value={form.ai_tool_id}
              onChange={(e) => set('ai_tool_id', e.target.value)}
            >
              <option value="">Select tool to migrate to...</option>
              {tools.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} — ${Number(t.monthly_price).toFixed(2)}/mo
                </option>
              ))}
            </select>
            {errors.ai_tool_id && <p className="text-xs text-red-500 mt-1">{errors.ai_tool_id}</p>}
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Start Date</label>
              <input
                className="input"
                type="date"
                value={form.start_date}
                onChange={(e) => set('start_date', e.target.value)}
              />
              {errors.start_date && <p className="text-xs text-red-500 mt-1">{errors.start_date}</p>}
            </div>
            <div>
              <label className="label">End Date <span className="text-gray-400 font-normal">(auto)</span></label>
              <input className="input bg-gray-50 text-gray-500" type="date" value={endDate} readOnly />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="label">Notes <span className="text-gray-400 font-normal">(optional)</span></label>
            <textarea
              className="input resize-none"
              rows={2}
              placeholder="Reason for migration..."
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
            />
          </div>

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
            <button type="submit" className="btn-primary flex-1" disabled={loading}>
              {loading ? 'Migrating...' : 'Confirm Migration'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default MigrateToolModal;
